import { ViewHook } from "phoenix_live_view";
import { debounce } from "../utils/debounce";
import { fetchSearchInitialContent, fetchSearchContent } from "../services/api";

const searchDialogShowEvent = "search-dialog:show";
const documentScrollPreventClass = "disable-body-scroll";

const debounceTime = 200;

// If we auto focus on mobile, the keyboard automatically opens and covers most of the
// trending sections.
const minWidthForAutoFocus = 768;

let cachedInitialContent: string | undefined;

class SearchDialog extends ViewHook {
  private container?: HTMLElement;

  private input?: HTMLInputElement;
  private closeButton?: HTMLButtonElement;

  private resultsContainer?: HTMLElement;

  private itemEventListeners?: Map<HTMLElement, (event: Event) => void>;

  mounted() {
    this.itemEventListeners = new Map();

    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    this.onInputChanged = this.onInputChanged.bind(this);
    this.onInputCleared = this.onInputCleared.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);

    this.container = this.el.querySelector("[data-search-dialog-container]") as HTMLElement;

    this.input = this.el.querySelector("input") as HTMLInputElement;
    this.closeButton = this.el.querySelector("[data-close-button]") as HTMLButtonElement;

    this.resultsContainer = this.el.querySelector("[data-results-container]") as HTMLElement;

    if (!this.container || !this.input || !this.closeButton || !this.resultsContainer) {
      throw new Error("SearchDialog: expected elements not found");
    }

    this.el.addEventListener(searchDialogShowEvent, this.show);
    this.input?.addEventListener("input", debounce(this.onInputChanged, debounceTime));
    this.input?.addEventListener("cleared", this.onInputCleared);
    this.closeButton.addEventListener("click", this.hide);

    if (this.el.dataset.initialState === "show") {
      void this.loadInitialContent().then(() => {
        this.show();
      });
    } else {
      void this.loadInitialContent();

      this.hide();
    }
  }

  destroyed() {
    this.hide();

    this.el.removeEventListener(searchDialogShowEvent, this.show);
    this.input?.removeEventListener("input", debounce(this.onInputChanged, debounceTime));
    this.input?.removeEventListener("cleared", this.onInputCleared);
    this.closeButton?.removeEventListener("click", this.hide);
  }

  private show() {
    this.showInitialContent();

    this.el.removeAttribute("hidden");
    document.documentElement.classList.add(documentScrollPreventClass);

    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("click", this.onClickOutside);

    if (window.innerWidth >= minWidthForAutoFocus) {
      this.input?.focus();
    }

    this.resultsContainer?.scrollTo({ top: 0, behavior: "instant" });
  }

  private hide() {
    this.el.setAttribute("hidden", "");
    document.documentElement.classList.remove(documentScrollPreventClass);

    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("click", this.onClickOutside);

    /* v8 ignore start */
    if (this.input) {
      this.input.value = "";
    }
    /* v8 ignore stop */

    this.clearItemEventListeners();
  }

  private onInputChanged() {
    const value = this.input?.value?.trim();
    if (value) {
      void fetchSearchContent(value).then((content) => {
        /* v8 ignore next */
        if (this.input?.value?.trim() === value) {
          this.showSearchResultsContent(content);
        }
      });
    } else {
      this.showInitialContent();
    }
  }

  private onInputCleared() {
    this.showInitialContent();
  }

  private onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.hide();
    }
  }

  private onClickOutside(event: MouseEvent) {
    const target = event.target as Node;

    const isOutside = this.container && !this.container.contains(target);

    if (isOutside) {
      this.hide();
    }
  }

  private showInitialContent() {
    if (cachedInitialContent) {
      this.showSearchResultsContent(cachedInitialContent);
    } else {
      void this.loadInitialContent().then((content) => {
        /* v8 ignore next */
        if (!this.input?.value?.trim()) {
          this.showSearchResultsContent(content);
        }
      });
    }
  }

  private async loadInitialContent() {
    cachedInitialContent ??= await fetchSearchInitialContent();

    return cachedInitialContent;
  }

  private showSearchResultsContent(content: string) {
    /* v8 ignore start */
    if (!this.resultsContainer) {
      return;
    }
    /* v8 ignore stop */

    this.clearItemEventListeners();

    this.resultsContainer.innerHTML = content;
    this.resultsContainer.removeAttribute("hidden");

    this.attachItemEventListeners();
  }

  private clearItemEventListeners() {
    this.itemEventListeners?.forEach((eventHandler, item) => {
      item.removeEventListener("click", eventHandler);
    });
    this.itemEventListeners?.clear();
  }

  private attachItemEventListeners() {
    /* v8 ignore start */
    if (!this.resultsContainer) {
      return;
    }
    /* v8 ignore stop */

    const items = this.resultsContainer.querySelectorAll<HTMLElement>("[data-path]");

    items.forEach((item) => {
      const eventHandler = (event: Event) => {
        this.onItemClick(event as MouseEvent, item);
      };

      item.addEventListener("click", eventHandler);

      this.itemEventListeners?.set(item, eventHandler);
    });
  }

  private onItemClick(event: MouseEvent, item: HTMLElement) {
    this.hide();

    window.location.href = item.dataset.path as string;
  }
}

export default SearchDialog;
